import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function PropertiesPage() {
  const [filter, setFilter] = useState('all');

  const comingSoonProperties = [
    {
      id: 1,
      type: 'short-term',
      title: 'Premium Short-Term Rental',
      comingSoon: true
    },
    {
      id: 2,
      type: 'short-term',
      title: 'Luxury Short-Term Stay',
      comingSoon: true
    },
    {
      id: 3,
      type: 'long-term',
      title: 'Quality Long-Term Rental',
      comingSoon: true
    },
    {
      id: 4,
      type: 'long-term',
      title: 'Professional Long-Term Home',
      comingSoon: true
    }
  ];

  const filteredProperties = filter === 'all' 
    ? comingSoonProperties 
    : comingSoonProperties.filter(property => property.type === filter);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-teal-600 via-blue-600 to-purple-700 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 slide-up">
            Our Properties
          </h1>
          <p className="text-xl md:text-2xl text-teal-50 max-w-3xl mx-auto slide-up">
            Discover quality rental properties throughout Eastern Pennsylvania
          </p>
        </div>
      </section>

      {/* Coming Soon Notice */}
      <section className="py-16 bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center space-x-4">
            <div className="w-12 h-12 bg-amber-400 rounded-full flex items-center justify-center">
              <i className="ri-time-line text-2xl text-white"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-amber-800 mb-2">Properties Coming Soon!</h2>
              <p className="text-amber-700">
                We're currently preparing our premium properties for launch. Check back soon or contact us to be notified when they become available.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 sm:mb-0">Available Properties</h2>
            
            <div className="flex space-x-2 bg-white rounded-lg p-1 shadow-md">
              <button
                onClick={() => setFilter('all')}
                className={`px-6 py-2 rounded-lg font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  filter === 'all'
                    ? 'bg-teal-500 text-white shadow-md'
                    : 'text-gray-600 hover:text-teal-600'
                }`}
              >
                All Properties
              </button>
              <button
                onClick={() => setFilter('short-term')}
                className={`px-6 py-2 rounded-lg font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  filter === 'short-term'
                    ? 'bg-teal-500 text-white shadow-md'
                    : 'text-gray-600 hover:text-teal-600'
                }`}
              >
                Short-Term
              </button>
              <button
                onClick={() => setFilter('long-term')}
                className={`px-6 py-2 rounded-lg font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  filter === 'long-term'
                    ? 'bg-teal-500 text-white shadow-md'
                    : 'text-gray-600 hover:text-teal-600'
                }`}
              >
                Long-Term
              </button>
            </div>
          </div>

          {/* Properties Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {filteredProperties.map((property, index) => (
              <div 
                key={property.id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 h-64 flex items-center justify-center">
                  <div className="text-center">
                    <i className="ri-home-4-line text-6xl text-gray-400 mb-4"></i>
                    <div className="bg-amber-400 text-white rounded-full px-4 py-2 inline-block">
                      <span className="text-sm font-bold">Coming Soon</span>
                    </div>
                  </div>
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1">
                    <span className={`text-sm font-semibold ${
                      property.type === 'short-term' ? 'text-teal-600' : 'text-blue-600'
                    }`}>
                      {property.type === 'short-term' ? 'Short-Term' : 'Long-Term'}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">{property.title}</h3>

                  <button className="w-full py-3 bg-gray-300 text-gray-500 font-semibold rounded-lg cursor-not-allowed whitespace-nowrap">
                    Notify Me When Available
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-teal-600 via-blue-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Be the First to Know</h2>
          <p className="text-xl text-teal-50 mb-8">
            Get notified when our premium properties become available for rent
          </p>
          <button 
            onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
            className="px-8 py-4 bg-white text-purple-700 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
          >
            Contact Us Now
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
